
import React from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { 
  Battery, 
  Signal, 
  Clock 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getStatusIcon, getStatusBadgeClass } from '../SmartMeterStatusHelpers';

interface SmartMeter {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  maintenanceStatus: {
    alerts: string[];
  };
}

interface SmartMeterTableRowProps {
  meter: SmartMeter;
  onSelectDevice: (deviceId: string) => void;
}

const SmartMeterTableRow: React.FC<SmartMeterTableRowProps> = ({ meter, onSelectDevice }) => {
  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <TableRow 
      key={meter.deviceId}
      className="cursor-pointer hover:bg-muted/80"
      onClick={() => onSelectDevice(meter.deviceId)}
    >
      <TableCell className="font-medium whitespace-nowrap">{meter.deviceId}</TableCell>
      <TableCell className="whitespace-nowrap">{meter.location}</TableCell>
      <TableCell>
        <div className="flex items-center">
          {getStatusIcon(meter.status)}
          <Badge className={cn("ml-1 text-xs", getStatusBadgeClass(meter.status))}>
            {meter.status}
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Battery className={cn(
                "h-4 w-4 mr-1",
                meter.batteryLevel > 70 ? "text-green-500" :
                meter.batteryLevel > 30 ? "text-amber-500" : "text-red-500"
              )} />
              {meter.batteryLevel}%
            </TooltipTrigger>
            <TooltipContent>
              <p>Battery Level: {meter.batteryLevel}%</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Signal className={cn(
                "h-4 w-4 mr-1",
                meter.signalStrength > 70 ? "text-green-500" :
                meter.signalStrength > 30 ? "text-amber-500" : "text-red-500"
              )} />
              {meter.signalStrength}%
            </TooltipTrigger>
            <TooltipContent>
              <p>Signal Strength: {meter.signalStrength}%</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="flex items-center">
          <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
          {formatDate(meter.lastCommunication)}
        </div>
      </TableCell>
      <TableCell>
        {meter.maintenanceStatus.alerts.length > 0 ? (
          <Badge variant="outline" className="text-xs text-red-500">
            {meter.maintenanceStatus.alerts.length} {meter.maintenanceStatus.alerts.length === 1 ? 'Alert' : 'Alerts'}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-green-500">
            No Alerts
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
};

export default SmartMeterTableRow;
